import React, { useState } from 'react';
import axios from 'axios';
import { API, formatError } from '../App';
import { toast } from 'sonner';

const Login = ({ onLoginSuccess }) => {
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'otp'
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [otpData, setOtpData] = useState({
    phone_number: '9773206254',
    otp: ''
  });
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [companyName, setCompanyName] = useState('BSCC Infrastructure');

  React.useEffect(() => {
    fetchCompanyName();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/settings/logo-status`);
      if (response.data.exists) {
        setLogoUrl(response.data.url);
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  };

  const fetchCompanyName = async () => {
    try {
      const response = await axios.get(`${API}/settings/company-name`);
      setCompanyName(response.data.company_name || '');
    } catch (error) {
      console.error('Error fetching company name:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleOTPChange = (e) => {
    setOtpData({
      ...otpData,
      [e.target.name]: e.target.value
    });
  };

  const handleSendOTP = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, {
        phone_number: otpData.phone_number
      });
      setOtpSent(true);
      toast.success(`OTP sent to ${otpData.phone_number}! OTP: ${response.data.otp}`);
    } catch (error) {
      console.error('Send OTP error:', error);
      toast.error(formatError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (loginMethod === 'email') {
        const response = await axios.post(`${API}/auth/login`, formData);

        const { access_token, user } = response.data;

        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));

        onLoginSuccess(); // Use the provided callback for navigation
      } else { // OTP Login
        if (otpSent) { // If OTP has been sent, verify it
          const response = await axios.post(`${API}/auth/verify-otp`, otpData);

          const { access_token, user } = response.data;

          localStorage.setItem('token', access_token);
          localStorage.setItem('user', JSON.stringify(user));

          onLoginSuccess(); // Use the provided callback for navigation
        } else { // If OTP not sent, send it
          const response = await axios.post(`${API}/auth/send-otp`, {
            phone_number: otpData.phone_number
          });
          if (response.data.status === 'success') {
            setOtpSent(true);
            toast.success('OTP sent successfully. Note: For testing use phone `1122334455` and OTP `1234`');
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(formatError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 via-blue-600 to-purple-700">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md" data-testid="login-container">
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            {logoUrl ? (
              <div className="w-24 h-24 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden mx-auto p-2">
                <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="p-4 bg-blue-100 rounded-full">
                <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-800 uppercase tracking-tight">{companyName}</h1>
          <p className="text-gray-600 mt-2">Attendance & Payroll Portal</p>
          <p className="text-sm text-gray-500 mt-1">Admin Login</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="flex border-b border-gray-200 mb-6">
            <button
              type="button"
              onClick={() => { setLoginMethod('email'); setOtpSent(false); }}
              className={`flex-1 py-2 text-sm font-medium ${loginMethod === 'email' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            >
              Email Login
            </button>
            {/* Hide OTP Login for now but keep the code
            <button
              type="button"
              onClick={() => setLoginMethod('otp')}
              className={`flex-1 py-2 text-sm font-medium ${loginMethod === 'otp' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            >
              OTP Login
            </button>
            */}
          </div>

          {loginMethod === 'email' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="admin@bscc.com"
                  data-testid="login-email"
                  required
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Enter your password"
                  data-testid="login-password"
                  required
                  autoComplete="current-password"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone_number"
                  value={otpData.phone_number}
                  onChange={handleOTPChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="9773206254"
                  data-testid="login-phone"
                  required
                  disabled
                />
              </div>

              {!otpSent ? (
                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={loading}
                  data-testid="send-otp-button"
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold py-3 px-4 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 disabled:opacity-50"
                >
                  {loading ? '📱 Sending...' : '📱 Send OTP'}
                </button>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      name="otp"
                      value={otpData.otp}
                      onChange={handleOTPChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-center text-2xl tracking-widest"
                      placeholder="000000"
                      data-testid="login-otp"
                      required
                      maxLength="6"
                      autoComplete="off"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="verify-otp-button"
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50"
                  >
                    {loading ? '⏳ Verifying...' : '✅ Verify OTP'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOtpSent(false)}
                    className="w-full text-blue-600 text-sm hover:text-blue-800"
                  >
                    Resend OTP
                  </button>
                </>
              )}
            </>
          )}

          {loginMethod === 'email' && (
            <button
              type="submit"
              disabled={loading}
              data-testid="login-button"
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </span>
              ) : (
                '🔐 Sign In'
              )}
            </button>
          )}
        </form>

        {/* Default credentials hidden as per request
        {loginMethod === 'email' && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 font-medium mb-2">📋 Default Admin Credentials:</p>
            <p className="text-xs text-blue-700">Email: <strong>admin@bscc.com</strong></p>
            <p className="text-xs text-blue-700">Password: <strong>NewTestPassword@123</strong></p>
            <p className="text-xs text-blue-600 mt-2">Please change your password after first login.</p>
          </div>
        )}
        */}

        {/* Hide OTP Login Info for now but keep the code
        {loginMethod === 'otp' && (
          <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-800 font-medium mb-2">📱 OTP Login:</p>
            <p className="text-xs text-green-700">Phone: <strong>9773206254</strong></p>
            <p className="text-xs text-green-600 mt-2">OTP will be sent to your registered mobile number.</p>
          </div>
        )}
        */}

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Secure admin access only. All actions are logged.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
